import { DateTime } from "luxon";
import {
  ActionType,
  EABShorthandTaskDeadline,
  EABShorthandTaskResponsible,
  EventType,
  type GetEABShorthandsForBoardFragmentFragment,
  type ICommonTask,
  useCreateTaskMutation,
  TaskTyp,
  EABShorthandTaskCustomAttributesType,
} from "@/graphql";
import { addAction } from "@/graphql/apolloCacheUtils";
import { defaultTask } from "@/components/event-action/task/helper/DefaultTask";
import store from "@/store";

export function getEventType(typename: string) {
  return typename as EventType;
}

function getShorthandTaskDeadline(shorthand: GetEABShorthandsForBoardFragmentFragment) {
  switch (shorthand.taskComponent.deadlineType) {
    case EABShorthandTaskDeadline.PRESENT:
      return new Date().toISOString();
    case EABShorthandTaskDeadline.FUTURE:
      return DateTime.now().plus({ days: shorthand.taskComponent.deadlineValue! }).toISO();
    case EABShorthandTaskDeadline.STATIC:
      return shorthand.taskComponent.deadlineDate!;
    case EABShorthandTaskDeadline.DYNAMIC:
      return undefined;
    default:
      throw Error("unknown shorthand deadline type");
  }
}

function getShorthandTaskAssigneeId(
  shorthand: GetEABShorthandsForBoardFragmentFragment,
  event: { owner?: { id: number } },
) {
  switch (shorthand.taskComponent.responsible) {
    case EABShorthandTaskResponsible.EVENT_OWNER:
      if (event.owner?.id == null) {
        throw Error("missing owner");
      }
      return event.owner?.id;
    case EABShorthandTaskResponsible.SELF:
      if (store.getters["login/isAnonym"]) {
        return undefined;
      }
      return store.getters["login/userId"];
    case EABShorthandTaskResponsible.STATIC:
      return shorthand.taskComponent.responsibleId!;
    case EABShorthandTaskResponsible.DYNAMIC:
      return undefined;
    default:
      throw Error("unknown shorthand responsible type");
  }
}

function getShorthandTaskMeta(shorthand: GetEABShorthandsForBoardFragmentFragment) {
  if (shorthand.taskComponent.customAttributes.length === 0) {
    // Common Task
    return undefined;
  }
  // Custom Task
  return {
    customAttributes: shorthand.taskComponent.customAttributes.reduce(
      (dictionary, next) => {
        dictionary[next.name] = {
          value: "",
          required: next.required,
          type: next.type,
        };
        return dictionary;
      },
      {} as Record<
        string,
        { value: string; required: boolean; type: EABShorthandTaskCustomAttributesType }
      >,
    ),
  };
}

function getTaskFromShorthand(
  shorthand: GetEABShorthandsForBoardFragmentFragment,
  event: { owner?: { id: number } },
  workflowNodeId?: string,
) {
  const task = defaultTask();
  task.status = shorthand.taskComponent.status;
  task.description = shorthand.taskComponent.description;
  task.deadline = getShorthandTaskDeadline(shorthand);
  task.assigneeId = getShorthandTaskAssigneeId(shorthand, event);
  task.drawFlowNodeId = workflowNodeId;
  task.type =
    shorthand.taskComponent.customAttributes.length === 0 ? TaskTyp.common : TaskTyp.CUSTOM;
  task.meta = getShorthandTaskMeta(shorthand);
  return task;
}

async function createTask(
  shorthand: GetEABShorthandsForBoardFragmentFragment,
  event: { owner?: { id: number }; id?: number; __typename?: string },
  mutate: ReturnType<typeof useCreateTaskMutation>["mutate"],
  workflowNodeId?: string,
) {
  const task = getTaskFromShorthand(shorthand, event, workflowNodeId) as ICommonTask;
  if (event.id == null) {
    throw Error("eventId missing. cant create task for shorthand");
  }
  await mutate(
    {
      eventId: event.id,
      eventType: getEventType(event.__typename!),
      task: {
        ...task,
      },
      commentUserId: store.getters["login/userId"],
    },
    {
      update: (cache, { data }) => {
        const storedObject = cache.identify({
          __typename: event.__typename,
          id: event.id,
        });
        if (storedObject && data != null) {
          addAction(storedObject, data.createCommonTask);
        }
      },
    },
  );
}

function isTaskShorthandSaveAble(shorthand: GetEABShorthandsForBoardFragmentFragment) {
  return (
    !store.getters["login/isAnonym"] &&
    shorthand.taskComponent.deadlineType !== EABShorthandTaskDeadline.DYNAMIC &&
    shorthand.taskComponent.responsible !== EABShorthandTaskResponsible.DYNAMIC &&
    shorthand.taskComponent.customAttributes.every((attr) => !attr.required)
  );
}

export function createShorthandAction(
  event: { owner?: { id: number }; id?: number; __typename?: string },
  mutate: ReturnType<typeof useCreateTaskMutation>["mutate"],
  workflowNodeId?: string,
  shorthand?: GetEABShorthandsForBoardFragmentFragment,
) {
  if (shorthand?.type != undefined) {
    switch (shorthand.type) {
      case ActionType.Task:
        if (isTaskShorthandSaveAble(shorthand)) {
          createTask(shorthand, event, mutate, workflowNodeId);
        } else {
          return getTaskFromShorthand(shorthand, event, workflowNodeId);
        }
        break;
      default:
        throw Error("unknown shorthand type");
    }
  }

  return undefined;
}
